/**
 * AgentTimeline - Horizontal timeline view of AI agent pipeline
 * Shows sequence: Parser → Classifier → Anomaly → Forensic → Attorney → Mediator → Report
 */

import React from 'react';
import { AgentStatus, PIPELINE_STAGES, AI_AGENTS } from './types';
import { CheckCircle, Loader2, Circle, AlertCircle, User } from 'lucide-react';

interface AgentTimelineProps {
  agents: AgentStatus[];
  currentAgent?: string;
  compact?: boolean;
  onAgentClick?: (agentId: string) => void;
}

export default function AgentTimeline({ agents, currentAgent, compact = false, onAgentClick }: AgentTimelineProps) {
  // Create a map of agent statuses
  const agentMap = new Map(agents.map(a => [a.agentId, a]));
  
  const getStatusIcon = (agentId: string) => {
    const agent = agentMap.get(agentId);
    if (!agent) return <Circle className="w-4 h-4 text-slate-300" />;
    
    switch (agent.status) {
      case 'complete':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'running':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'waiting_human':
        return <User className="w-4 h-4 text-amber-500" />;
      default:
        return <Circle className="w-4 h-4 text-slate-300" />;
    }
  };
  
  const getLineColor = (index: number) => {
    const agentId = PIPELINE_STAGES[index];
    const agent = agentMap.get(agentId);
    if (agent?.status === 'complete') return 'bg-emerald-400';
    if (agent?.status === 'running') return 'bg-blue-400';
    return 'bg-slate-200';
  };
  
  if (compact) {
    // Compact inline version for lists
    return (
      <div className="flex items-center gap-1">
        {PIPELINE_STAGES.slice(0, 5).map((agentId, index) => {
          const agentDef = AI_AGENTS[agentId];
          const agent = agentMap.get(agentId);
          const isActive = agent?.status === 'running';
          
          return (
            <React.Fragment key={agentId}>
              <span 
                className={`
                  cursor-pointer transition-transform hover:scale-110
                  ${isActive ? 'animate-pulse' : ''}
                  ${!agent || agent.status === 'idle' || agent.status === 'queued' ? 'opacity-40' : ''}
                `}
                title={`${agentDef?.agentName}: ${agent?.status || 'queued'}`}
                onClick={() => onAgentClick?.(agentId)}
              >
                {agentDef?.icon}
              </span>
              {index < 4 && (
                <span className={`w-2 h-0.5 ${getLineColor(index)}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
  
  // Full timeline view
  return (
    <div className="w-full">
      {/* Timeline header */}
      <div className="text-xs font-medium text-slate-500 mb-3">AI PROCESSING TIMELINE</div>
      
      {/* Timeline nodes */}
      <div className="relative">
        {/* Connecting line */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-slate-200" />
        
        {/* Nodes */}
        <div className="relative flex justify-between">
          {PIPELINE_STAGES.map((agentId, index) => {
            const agentDef = AI_AGENTS[agentId];
            const agent = agentMap.get(agentId);
            const isActive = agent?.status === 'running';
            const isCurrent = currentAgent === agentId;
            
            return (
              <div 
                key={agentId}
                className={`
                  flex flex-col items-center cursor-pointer transition-all
                  ${isCurrent ? 'scale-110' : 'hover:scale-105'}
                `}
                onClick={() => onAgentClick?.(agentId)}
              >
                {/* Node circle */}
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-lg
                  ${agent?.status === 'complete' ? 'bg-emerald-100' : ''}
                  ${agent?.status === 'running' ? 'bg-blue-100 ring-2 ring-blue-400 ring-offset-2' : ''}
                  ${agent?.status === 'waiting_human' ? 'bg-amber-100' : ''}
                  ${agent?.status === 'error' ? 'bg-red-100' : ''}
                  ${!agent || agent.status === 'idle' || agent.status === 'queued' ? 'bg-slate-100' : ''}
                `}>
                  {agentDef?.icon}
                </div>
                
                {/* Status icon */}
                <div className="mt-1">
                  {getStatusIcon(agentId)}
                </div>
                
                {/* Label */}
                <div className="mt-1 text-center">
                  <div className="text-[10px] font-medium text-slate-700 truncate max-w-[60px]">
                    {agentDef?.agentName.replace(' AI', '').split(' ')[0]}
                  </div>
                  {agent?.progress && isActive && (
                    <div className="text-[9px] text-blue-600">
                      {agent.progress.percentage}%
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Current status text */}
      {currentAgent && (
        <div className="mt-4 text-sm text-slate-600 text-center">
          <span className="font-medium">Currently:</span>{' '}
          {AI_AGENTS[currentAgent]?.agentName}
          {agentMap.get(currentAgent)?.progress && (
            <span className="text-blue-600 ml-1">
              ({agentMap.get(currentAgent)!.progress!.current} of {agentMap.get(currentAgent)!.progress!.total})
            </span>
          )}
        </div>
      )}
    </div>
  );
}
